import { Component, computed, inject, signal } from '@angular/core';
import { Router, ActivatedRoute, RouterLink, ParamMap } from '@angular/router';
import { FormBuilder, Validators, ReactiveFormsModule, ValidatorFn, AbstractControl } from '@angular/forms';
import { FirebaseCodeErrorService } from './../../services/firebase-code-error.service';
import { ErrorReporteService } from 'src/app/services/error-reporte.service';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { firstValueFrom } from 'rxjs';
import { UsuarioService } from 'src/app/services/usuario.service';

@Component({
    selector: 'app-sign-up',
    templateUrl: './sign-up.component.html',
    styleUrls: ['./sign-up.component.css'],
    standalone: false
})
export class SignUpComponent {
  private _authService = inject(AuthService);
  private _firebaseError = inject(FirebaseCodeErrorService);
  private _errorReporte = inject(ErrorReporteService);
  private _usuarioService = inject(UsuarioService);
  private router = inject(Router);
  private _route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private _loadingService = inject(LoadingService);

  // Estado (signals)
  error = signal('');
  loading = computed(() => this._loadingService.isLoading());
  fieldTextType = signal(false);
  mostrarMensaje = signal(false);

  // idPerfil (si viene por ruta /login/sign-up/:idPerfil)
  idPerfil = signal<number>(Number(this._route.snapshot.paramMap.get('idPerfil')) || 2);

  // Texto del perfil derivado del id (computed)
  perfil = computed(() => {
    switch (this.idPerfil()) {
      case 2: return 'Usuario';
      case 3: return 'Prestador';
      case 4: return 'Comercio';
      default: return 'Usuario';
    }
  });

  // Form reactivo tipado (nonNullable)
  readonly registrarUsuario = this.fb.nonNullable.group({
    email: this.fb.nonNullable.control('', [Validators.required, Validators.email]),
    // arranca SIN required; lo ponemos dinámico según perfil
    nro_socio: this.fb.nonNullable.control('', []),
    clave: this.fb.nonNullable.control('', [
      Validators.required,
      Validators.minLength(6),
      Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/) // minúscula, mayúscula y número
    ]),
    repetirClave: this.fb.nonNullable.control('', [Validators.required]),
    // requiredTrue va en el control, no en el form group
    aceptaTyC: this.fb.nonNullable.control(false, Validators.requiredTrue),
  }, {
    // El validador de coincidencia va a nivel de grupo
    validators: passwordsMatchValidator('clave', 'repetirClave')
  });

  toggleFieldTextType() {
    this.fieldTextType.update(v => !v);
  }

  mostrarRequisitos()  { 
    this.mostrarMensaje.set(true);  
  }
  ocultarRequisitos()  {
     this.mostrarMensaje.set(false); 
  }

  ngOnInit(): void {
    // aplicar al iniciar
    this.applyPerfilRules(this.idPerfil());

    // y cada vez que cambie la ruta
    // Suscribirse a cambios del parámetro (por si navegan entre /sign-up/:id)
    this._route.paramMap.subscribe((params: ParamMap) => {
      const id = Number(params.get('idPerfil')) || 2;
      this.idPerfil.set(id); // actualizar signal
      this.applyPerfilRules(id);
      // `perfil()` se recalcula solo por el computed
    });
  }

  private applyPerfilRules(idPerfil: number) {
    const nro = this.registrarUsuario.controls.nro_socio;

    if (idPerfil === 2) {
      nro.enable({ emitEvent: false });
      nro.setValidators([
        Validators.required,
        Validators.minLength(4),
        Validators.maxLength(8),
      ]);
    } else {
      // no aplica: lo limpiamos y lo sacamos de la ecuación
      nro.clearValidators();
      nro.setValue('', { emitEvent: false });
      nro.disable({ emitEvent: false });
      nro.markAsUntouched();
      nro.markAsPristine();
    }

    nro.updateValueAndValidity({ emitEvent: false });
  }

  async registrar() {
    // Evita doble submit si ya está cargando
    if (this.loading() || this.registrarUsuario.pending) return;

    // Dispara validaciones en todos los campos
    this.registrarUsuario.markAllAsTouched();
    if (this.registrarUsuario.invalid) {
      this.error.set('Debe completar los campos requeridos y aceptar los Términos.');
      return;
    }

    const { email, clave, nro_socio } = this.registrarUsuario.getRawValue();
    const emailNorm = email.trim().toLowerCase(); // normalizar

    const esCliente = this.idPerfil() === 2;

    // Validación básica de nro_socio (por si no está ya como requerido en el form)
    // solo para perfil cliente
    const nroSocioLimpio = esCliente
      ? (nro_socio ?? '').toString().replace(/\D/g, '')
      : '';

    try {
      this.error.set('');
      this.registrarUsuario.disable({ emitEvent: false }); // bloquear form
      await this._loadingService.run(async () => {
        // 1) Chequear si es socio válido
        if (esCliente) {
          if (!nroSocioLimpio) {
            throw { code: 'app/invalid-member', message: 'Debe ingresar su Número de Socio.' };
          }

          let res: any;
          try {
            res = await firstValueFrom(
              this._usuarioService.checkAsociadoXNroSocio(nroSocioLimpio)
            );
          } catch (apiErr: any) {
            // La API de Cardio24hs (externa, no pasa por nuestro backend) devuelve
            // HTTP 400 ("No hay dni cargado") cuando el número de socio no existe,
            // en vez de un 200 con habilitado:false. NO es una falla del servicio:
            // es su forma de decir "no es socio" — se trata igual que inválido, sin
            // reportar. Cualquier otro error (red, timeout, 5xx) sí es una falla real.
            if (apiErr?.status === 400) {
              throw {
                code: 'app/invalid-member',
                message: 'El Número de Socio ingresado no es válido o no se encuentra habilitado.',
              };
            }
            throw {
              code: 'app/socio-check-failed',
              message: 'No se pudo validar tu número de socio en este momento. Reintentá más tarde.',
              causa: apiErr,
            };
          }

          const habilitado = !!res?.habilitado;
          if (!habilitado) {
            throw {
              code: 'app/invalid-member',
              message: 'El Número de Socio ingresado no es válido o no se encuentra habilitado.',
            };
          }
        }
        
        // 2) Registrar en Firebase/backend
        await this._authService.register(emailNorm, clave, this.idPerfil(), nroSocioLimpio);
      });
      // Éxito → avisamos y vamos a verificar email
      this._firebaseError.showSuccess('Usuario registrado con éxito. Revisá tu email para verificar la cuenta.', 5000);
      await this.router.navigateByUrl('/verify-email');
    } catch (err: any) {
      // Socio inválido / no habilitado → mensaje específico, no se reporta (no es un fallo)
      if (err?.code === 'app/invalid-member') {
        this.error.set(err.message);
      } else if (err?.code === 'app/socio-check-failed') {
        // Falló la API externa de validación de socio → reporte marcado como tal
        const ref = this._errorReporte.reportar('sign-up-validacion-socio', err.causa ?? err, { perfil: this.idPerfil(), email: emailNorm });
        this.error.set(`${err.message} (ref: ${ref})`);
      } else {
        const code = this._firebaseError.extractCode(err);
        const msg = this._firebaseError.codeError(code) || 'Ocurrió un error al registrar';
        if (msg.includes('desconocido')) {
          // No lo pudimos identificar (no es EMAIL_EXISTS, weak-password, etc.):
          // esto sí puede ser un bug real, se reporta con stack + email de aviso.
          const ref = this._errorReporte.reportar('sign-up', err, { perfil: this.idPerfil(), email: emailNorm });
          this.error.set(`No pudimos completar el registro. El problema quedó registrado; probá de nuevo en unos minutos o escribinos si sigue pasando. (ref: ${ref})`);
        } else {
          // Error "conocido" (email ya existe, clave débil, etc.): es un resultado
          // normal del formulario, no una falla del sistema — no se reporta.
          this.error.set(msg);
        }
      }
    } finally {
      this.registrarUsuario.enable({ emitEvent: false }); // re-habilitar form
      // re-aplicar reglas porque el enable() habilita todo el form
      this.applyPerfilRules(this.idPerfil());
    }
  }

  async signUpXPerfil(id_perfil: number) {
    this.error.set('');
    // Muestra el loading mientras se ejecuta la función async que le pasamos
    await this._loadingService.run(async () => {
      // pequeña demora "visual"
      await this.sleep(500);
      await this.router.navigate(['/sign-up', id_perfil]);
    });
  }

  private sleep(ms: number) {
    return new Promise<void>(resolve => setTimeout(resolve, ms));
  }
}

/** Validador de coincidencia de contraseñas */
function passwordsMatchValidator(passKey: string, repeatKey: string): ValidatorFn {
  return (group: AbstractControl) => {
    const pass = group.get(passKey)?.value;
    const rep  = group.get(repeatKey)?.value;
    return pass === rep ? null : { passwordsMismatch: true };
  };
}
